import React, { useState } from 'react';
import { Button, Chip, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CouponOtherStatus } from '../../services/payment.api';

interface CouponCodeFieldProps {
  codes: string[];
  maxCodes: number;
  disabled?: boolean;
  others?: { code: string; status: CouponOtherStatus }[];
  bestCode?: string | null;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
}

export const CouponCodeField: React.FC<CouponCodeFieldProps> = ({
  codes,
  maxCodes,
  disabled,
  others = [],
  bestCode,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const statusOf = (code: string) => others.find((item) => item.code === code)?.status;

  const submit = () => {
    if (!value.trim()) return;
    onAdd(value);
    setValue('');
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <TextField
          size="small"
          fullWidth
          value={value}
          disabled={disabled || codes.length >= maxCodes}
          placeholder={t('subscription.coupon.placeholder', 'Discount code')}
          inputProps={{
            maxLength: 64,
            'aria-label': t('subscription.coupon.label', 'Discount code'),
          }}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button
          variant="outlined"
          onClick={submit}
          disabled={disabled || !value.trim() || codes.length >= maxCodes}
        >
          {t('subscription.coupon.apply', 'Check code')}
        </Button>
      </div>
      <p className="text-xs text-gray-500 dark:text-slate-400">
        {t('subscription.coupon.hint', 'A code is only used when you confirm the purchase.')}
      </p>
      {codes.length > 0 && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-2">
            {codes.map((code) => {
              const status = statusOf(code);
              const isBest = code === bestCode;
              const invalid = status && status !== 'not_applied_lower';
              return (
                <Chip
                  key={code}
                  label={code}
                  size="small"
                  color={isBest ? 'success' : invalid ? 'error' : 'default'}
                  variant={isBest ? 'filled' : 'outlined'}
                  onDelete={disabled ? undefined : () => onRemove(code)}
                />
              );
            })}
          </div>
          {others.map((item) => (
            <p
              key={item.code}
              className={
                item.status === 'not_applied_lower'
                  ? 'text-xs text-gray-500 dark:text-slate-400'
                  : 'text-xs text-red-600 dark:text-red-400'
              }
            >
              {item.code}:{' '}
              {t(
                `subscription.coupon.status.${item.status}`,
                item.status === 'not_applied_lower'
                  ? 'Not applied together with a better code'
                  : 'This code cannot be used'
              )}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
